import jwtDecode from "jwt-decode"
import authentication from "@irati/auth"
import channel from "../helpers/channel"
import intercomTokenService from "./token"

export type IntercomIdentityToken = {type: "jwt"; jwt: string} | {type: "userHash"; userHash: string}

export interface ScheduleIntercomJwtRefreshParams {
  jwt: string
  onRefresh: (jwt: string) => void
}

// refresh ahead of expiry so the messenger never holds an expired jwt
const REFRESH_AHEAD_MS = 5 * 60 * 1000
const MIN_REFRESH_DELAY_MS = 60 * 1000

let refreshTimer: ReturnType<typeof setTimeout> | undefined

/**
 * Prefers a Messenger Security JWT, falling back to the legacy Identity Verification user hash
 * while INTERCOM_JWT_SECRET is not configured in auth-app. Ghost sessions are never identified.
 * TODO(US-3920): remove the user hash fallback once Messenger Security is enforced on all platforms
 */
export async function getIntercomIdentityToken(): Promise<IntercomIdentityToken | null> {
  if (authentication.isUserGhost()) {
    return null
  }

  try {
    const jwt = await intercomTokenService.getJwt(channel)
    if (jwt) {
      return {type: "jwt", jwt}
    }
  } catch (err) {
    /* fall back to the user hash */
  }

  const userHash = await intercomTokenService.get(channel)
  return userHash ? {type: "userHash", userHash} : null
}

export function scheduleIntercomJwtRefresh({jwt, onRefresh}: ScheduleIntercomJwtRefreshParams): void {
  cancelIntercomJwtRefresh()

  refreshTimer = setTimeout(async () => {
    let next: string | null = null
    try {
      next = await intercomTokenService.getJwt(channel)
    } catch (err) {
      /* retried below */
    }

    if (next) {
      onRefresh(next)
    }
    scheduleIntercomJwtRefresh({jwt: next || jwt, onRefresh})
  }, refreshDelay(jwt))
}

export function cancelIntercomJwtRefresh(): void {
  clearTimeout(refreshTimer)
  refreshTimer = undefined
}

function refreshDelay(jwt: string) {
  try {
    const {exp} = jwtDecode<{exp: number}>(jwt)
    return Math.max(exp * 1000 - Date.now() - REFRESH_AHEAD_MS, MIN_REFRESH_DELAY_MS)
  } catch (err) {
    // unreadable token, retry soon
    return MIN_REFRESH_DELAY_MS
  }
}
